import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  TextField,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { isUndefined } from "lodash";
import CompanyWarningPopup from "../../../../app/features/CompanyCreate/companycreatePopup";
import { useNavigate } from "react-router-dom";
import StatusTabs from "../../../order/components/view/status.tabs";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableComponent from "@/app/global-components/table";
import moment from "moment";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import { routes } from "@/consts";
import useOrder from "@/hooks/use-order";
import useRegisterCompany from "@/hooks/use-register-company";
interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  address: string;
  products: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  status: string;
  tender: { title: string };
  company: {
    CompanyNr: string;
    nameAr: string;
  };
}

const OrderBuylist: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { loading, error, fetchBuyerOrders } = useOrder();
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;
  const [isCompanyAvailable, setIsCompanyAvailable] = useState(false);
  const { getCompanyMine } = useRegisterCompany();

  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine().catch(() => {
        setIsCompanyAvailable(true);
      });
    };
    fetchCompany();
  }, []);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const isMobile = useMediaQuery("(max-width:600px)");

  const [statuses, setStatuses] = useState([
    { label: t("status.planning"), name: "Planning", total: 0 },
    { label: t("general.purchaseRequest"), name: "Opened", total: 0 },
    //{ label: t("status.closed"), name: "Closed", total: 0 },
    { label: t("status.accepted"), name: "Accepted", total: 0 },
    // { label: t("status.payment"), name: "Payment", total: 0 },
    // { label: t("status.paymentConfirmation"), name: "PaymentConfirmation", total: 0 },
    { label: t("status.shipping"), name: "Sending", total: 0 },
    { label: t("status.received"), name: "Receiving", total: 0 },
    { label: t("status.completed"), name: "Finished", total: 0 },
    { label: t("general.reviewRequest"), name: "Revision", total: 0 },
    { label: t("status.rejected"), name: "Canceled", total: 0 },
  ]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (myCompany?.id) {
          const result = await fetchBuyerOrders(myCompany.id);
          setOrders(result?.data || []);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };
    fetchOrders();
  }, [myCompany?.id]);

  useEffect(() => {
    const updatedStatuses = statuses.map((status) => ({
      ...status,
      total: orders.filter((order) => order.status === status.name).length,
    }));
    setStatuses(updatedStatuses);
  }, [orders]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredOrders = orders
    .filter((order) => {
      if (selectedStatus) {
        return order.status === selectedStatus;
      }
      return true;
    })
    .filter((order) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        order?.OrderNr?.toString().includes(searchLower) ||
        order.title.toLowerCase().includes(searchLower) ||
        order.company?.CompanyNr.toLowerCase().includes(searchLower) ||
        order.tender?.title.toLowerCase().includes(searchLower)
      );
    });

  const calculateTotalPrice = (products: Product[]) =>
    products.reduce(
      (sum, product) => sum + product.price * product.quantity,
      0
    );

  const getStatusLabel = (status: string) => {
    const statusItem = statuses.find((s) => s.name === status);
    return statusItem ? statusItem.label : t("status.unknown");
  };

  const handleNavigateToOrder = (orderId: string) => {
    navigate(`${routes.order}/${orderId}`);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedStatus(newValue);
  };

  const columns = [
    { title: t("orders.number"), field: "OrderNr" },
    {
      title: t("general.tender"),
      field: "tender.title",
      render: (row: Order) => row.tender?.title || "N/A",
      hideOnMobile: true,
    },
    {
      title: t("company.fields.name"),
      field: "company.nameAr",
      render: (row: Order) => row.company?.CompanyNr || "N/A",
    },
    { title: t("orders.title"), field: "title" },
    {
      title: t("orders.fields.deliveryDate"),
      field: "deliverDate",
      hideOnMobile: true,
      render: (row: Order) => moment(row.deliverDate).format("YYYY-MM-DD"),
    },
    {
      title: t("company.fields.address"),
      field: "address",
      hideOnMobile: true,
    },
    {
      title: t("orders.fields.totalPrice"),
      field: "totalPrice",
      render: (row: Order) =>
        `${calculateTotalPrice(row.products).toFixed(2)} ${t(
          "checkout.summary.currency"
        )}`,
    },
    {
      title: t("orders.fields.status"),
      field: "status",
      render: (row: Order) => getStatusLabel(row.status),
    },
    {
      title: t("general.actions"),
      field: "actions",
      render: (row: Order) => (
        <IconButton
          color="inherit"
          size="small"
          onClick={() => handleNavigateToOrder(row.id)}
          title={t("general.view")}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];
  const handleRowClick = (row: Order) => {
    handleNavigateToOrder(row.id);
  };
  const paginatedOrders = filteredOrders;

  return (
    <Box sx={{ p: 3 }}>
      {isCompanyAvailable && <CompanyWarningPopup open={true} />}

      <h2 className="flex text-3xl font-semibold tracking-tight mb-2 text-primary-dark">
        {t("orders.listTitle")}{" "}
        {userRole === ROLE.SELLER ? t("general.sales") : t("general.Purchases")}
      </h2>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("alerts.error.fetching")}{" "}
          {userRole === ROLE.SELLER
            ? t("general.sales")
            : t("general.Purchases")}
          .
        </Alert>
      )}

      {!loading && !error && (
        <>
          <StatusTabs
            statuses={statuses}
            selectedStatus={selectedStatus}
            handleTabChange={handleTabChange}
            orderTotal={orders.length}
          />

          <TextField
            label={t("actions.search")}
            variant="outlined"
            fullWidth
            dir={locale === LANG.AR ? "rtl" : "ltr"}
            margin="normal"
            value={searchTerm}
            size="small"
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
              inputProps: { dir: locale === LANG.AR ? "rtl" : "ltr" },
            }}
            sx={{
              maxWidth: 300,
              "& .MuiInputBase-root": {
                direction: locale === LANG.AR ? "rtl" : "ltr",
              },
              "& .MuiInputLabel-root": {
                transformOrigin: locale === LANG.AR ? "right" : "left",
                direction: locale === LANG.AR ? "rtl" : "ltr",
                // right: locale === LANG.AR ? 30 : "auto",
                // left: locale === LANG.EN ? 0 : "auto",
                ...(locale === LANG.AR
                  ? { right: 30, left: "auto" }
                  : { left: 0, right: "auto" }),
              },
              "& legend": {
                textAlign: locale === LANG.AR ? "end" : "start",
              },
            }}
          />

          <TableComponent
            columns={columns}
            data={paginatedOrders}
            rowKeyField="id"
            isMobile={isMobile}
            maxHeight={400}
            pagination={true}
            rowsPerPage={itemsPerPage}
            onRowClick={handleRowClick}
          />
        </>
      )}
    </Box>
  );
};

export default OrderBuylist;
